import { useEffect, useRef, useState } from 'react'
import type { FC, ReactNode } from 'react'
import { ArrowUp, ChevronLeft, ChevronRight } from 'lucide-react'
import styles from './ListPagerFooter.module.css'
import { createRootVarWriter, frameThrottle } from '../../utils/rootCssVars'
import { bottomNavOffsetCss } from '../../utils/bottomNavOffset'

interface ListPagerFooterProps {
  visible?: boolean
  info: ReactNode
  canPrev?: boolean
  canNext?: boolean
  onPrev?: () => void
  onNext?: () => void
  onBackToTop?: () => void
  prevLabel?: string
  nextLabel?: string
  maxWidth?: number | string
  pageSize?: number
  pageSizeOptions?: readonly number[]
  onPageSizeChange?: (value: number) => void
  currentPage?: number
  totalPages?: number
  onPageSelect?: (page: number) => void
  primaryAction?: ReactNode
  compactOnNarrowScreens?: boolean
}

export const ListPagerFooter: FC<ListPagerFooterProps> = ({
  visible = true,
  info,
  canPrev = false,
  canNext = false,
  onPrev,
  onNext,
  onBackToTop,
  prevLabel = 'Prev',
  nextLabel = 'Next',
  maxWidth = 1200,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  currentPage,
  totalPages,
  onPageSelect,
  primaryAction,
  compactOnNarrowScreens = false,
}) => {
  const footerRef = useRef<HTMLDivElement | null>(null)
  const [pageInput, setPageInput] = useState(() => String(currentPage ?? ''))

  useEffect(() => {
    if (typeof currentPage === 'number' && Number.isFinite(currentPage)) {
      setPageInput(String(currentPage))
    }
  }, [currentPage])

  useEffect(() => {
    const root = document.documentElement
    const appShellNav = document.querySelector('[data-app-shell-nav="true"]') as HTMLElement | null

    // Measure first, then write only what changed, at most once a frame (see utils/rootCssVars).
    const vars = createRootVarWriter(root)
    const recalculate = () => {
      const height = footerRef.current?.offsetHeight ?? 0
      vars.set('--list-pager-base-offset', bottomNavOffsetCss(appShellNav))
      vars.set('--list-pager-height', `${height}px`)
    }
    const throttled = frameThrottle(recalculate)

    recalculate()

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
          throttled.schedule()
        })
      : null

    if (resizeObserver && footerRef.current) resizeObserver.observe(footerRef.current)
    if (resizeObserver && appShellNav) resizeObserver.observe(appShellNav)

    window.addEventListener('resize', throttled.schedule, { passive: true })
    window.visualViewport?.addEventListener('resize', throttled.schedule)
    window.visualViewport?.addEventListener('scroll', throttled.schedule)

    return () => {
      throttled.cancel()
      resizeObserver?.disconnect()
      window.removeEventListener('resize', throttled.schedule)
      window.visualViewport?.removeEventListener('resize', throttled.schedule)
      window.visualViewport?.removeEventListener('scroll', throttled.schedule)
      vars.remove('--list-pager-height')
      vars.remove('--list-pager-base-offset')
    }
  }, [])

  const handlePageJump = () => {
    if (!onPageSelect || typeof currentPage !== 'number' || typeof totalPages !== 'number') return

    const parsedPage = Number.parseInt(pageInput, 10)
    if (!Number.isFinite(parsedPage)) {
      setPageInput(String(currentPage))
      return
    }

    const nextPage = Math.min(Math.max(parsedPage, 1), Math.max(totalPages, 1))
    setPageInput(String(nextPage))

    if (nextPage !== currentPage) {
      onPageSelect(nextPage)
    }
  }

  if (!visible) return null

  return (
    <div
      ref={footerRef}
      className={`${styles.footer} ${compactOnNarrowScreens ? styles.footerCompactOnNarrow : ''}`}
    >
      <div className={styles.inner} style={{ maxWidth }}>
        <div className={styles.info}>{info}</div>

        <div className={styles.actions}>
          {primaryAction}

          {typeof pageSize === 'number' && Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 && onPageSizeChange && (
            <label className={`${styles.selectLabel} ${styles.perPage}`}>
              <span className={styles.selectLabelText}>Per page</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className={styles.select}
              >
                {pageSizeOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          )}

          {typeof currentPage === 'number' && typeof totalPages === 'number' && totalPages > 1 && onPageSelect && (
            <div className={styles.pageJump}>
              <label className={styles.selectLabel}>
                <span className={styles.selectLabelText}>Page</span>
                <input
                  type="number"
                  min={1}
                  max={Math.max(totalPages, 1)}
                  step={1}
                  value={pageInput}
                  onChange={(e) => setPageInput(e.target.value)}
                  onBlur={handlePageJump}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter') return
                    event.preventDefault()
                    handlePageJump()
                  }}
                  className={styles.pageInput}
                  aria-label={`Page number, current page ${currentPage} of ${totalPages}`}
                />
              </label>
              <span className={styles.pageJumpMeta}>of {totalPages}</span>
              <button type="button" className={`${styles.button} ${styles.goButton}`} onClick={handlePageJump}>
                Go
              </button>
            </div>
          )}

          {onBackToTop && (
            <button type="button" className={`${styles.button} ${styles.backToTop}`} onClick={onBackToTop} aria-label="Back to top" title="Back to top">
              <ArrowUp size={16} aria-hidden="true" />
              <span className={styles.buttonLabel}>Back to Top</span>
            </button>
          )}

          {onPrev && (
            <button type="button" className={`${styles.button} ${styles.prevButton}`} onClick={onPrev} disabled={!canPrev} aria-label={prevLabel} title={prevLabel}>
              <ChevronLeft size={16} aria-hidden="true" />
              <span className={styles.buttonLabel}>{prevLabel}</span>
            </button>
          )}

          {onNext && (
            <button type="button" className={`${styles.button} ${styles.nextButton}`} onClick={onNext} disabled={!canNext} aria-label={nextLabel} title={nextLabel}>
              <span className={styles.buttonLabel}>{nextLabel}</span>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ListPagerFooter
